import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export const THEME_STORAGE_KEY = "cedcreate-theme";

const ThemeContext = createContext(null);

const getStoredTheme = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch (e) {}

  return "system";
};

const getSystemPrefersDark = () =>
  window.matchMedia("(prefers-color-scheme: dark)").matches;

export const ThemeProvider = ({ children }) => {
  // Static defaults so SSR and the first client render match.
  const [theme, setTheme] = useState("system");
  const [systemPrefersDark, setSystemPrefersDark] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const resolvedTheme = useMemo(() => {
    if (theme === "system") {
      return systemPrefersDark ? "dark" : "light";
    }
    return theme;
  }, [theme, systemPrefersDark]);

  useEffect(() => {
    setTheme(getStoredTheme());
    setSystemPrefersDark(getSystemPrefersDark());
    setIsReady(true);

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event) => {
      setSystemPrefersDark(event.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (!isReady) return;

    document.documentElement.classList.toggle("dark", resolvedTheme === "dark");

    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (e) {}
  }, [theme, resolvedTheme, isReady]);

  const setThemePreference = (nextTheme) => {
    if (nextTheme === "light" || nextTheme === "dark" || nextTheme === "system") {
      setTheme(nextTheme);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        resolvedTheme,
        setTheme: setThemePreference,
        isDark: resolvedTheme === "dark",
        isSystem: theme === "system",
        isReady,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
